/**
 * Application utilities for handling the new consolidated schema
 */

export interface ApplicationDetail {
  // Part I - Entry Information
  companyNameEng: string;
  companyNameChi?: string;
  entryTitleEng: string;
  entryTitleChi?: string;
  entryDescription: string;
  companyDescription: string;
  businessRegNo: string;
  incorporationNo: string;
  incorporationDate: string;
  companyAddress: string;
  companyCEO?: string;
  companyBusiness: string;
  companyURL: string;
  isStartup: string;
  employeeCount?: string;
  incubationProgram: string;
  incubationDescription?: string;

  // File URLs
  logoAIUrl?: string;
  logoJPEGUrl?: string;
  brandGuidelineUrl?: string;
  entryPhotoUrl?: string;
  supportingDocumentUrl?: string;

  // Part II - Contact Details
  primaryContactName: string;
  primaryContactTitle: string;
  primaryContactPhone: string;
  primaryContactEmail: string;
  secondaryContactName: string;
  secondaryContactTitle: string;
  secondaryContactPhone: string;
  secondaryContactEmail: string;

  // Part III - Award Categories
  awardCategory: string;

  // Part IV - Judging Materials
  innovationCreativity: string;
  functionalityErgonomics: string;
  quality: string;
  benefitsImpact: string;
  marketPotential: string;
  ecologicalCompatibility: string;
  videoLink?: string;

  // Declaration
  submissionUsage: boolean;
  applicationDeclaration: boolean;
  patentStatus: string;
  patentNumber?: string;
  patentTitle?: string;
  patentProprietor?: string;
  patentCountry?: string;
  patentDate?: string;
  patentStatusType?: string;
  intellectualProperty: string;
  disputeDescription?: string;
  eventAdminCost: boolean;
  verifyCode: string;
}

export interface ApplicationData {
  id?: string;
  applicationDetail: ApplicationDetail;
  applicationStatus: string;
  status: string;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/**
 * Generate a 9-digit application ID starting with '2025'
 * Format: 2025XXXXX where XXXXX is a 5-digit sequential number
 *
 * TODO: After deploying the generateApplicationId Lambda function,
 * update this to use client.queries.generateApplicationId()
 */
export const generateApplicationId = async (): Promise<string> => {
  try {
    // For now, use a timestamp-based approach until the schema is deployed
    const timestamp = Date.now();
    const randomSuffix = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    const applicationId = `2025${timestamp.toString().slice(-5)}${randomSuffix}`;

    return applicationId;
  } catch (error) {
    console.error('Error generating application ID:', error);
    // Fallback: use timestamp-based ID if database query fails
    const timestamp = Date.now();
    const randomSuffix = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `2025${timestamp.toString().slice(-5)}${randomSuffix}`;
  }
};

/**
 * Submit application using the application pipeline
 * This function handles the entire process: submit → generate ID → send email
 *
 * TODO: After deploying the applicationPipeline Lambda function,
 * update this to use client.mutations.applicationPipeline()
 */
export const submitApplication = async (): Promise<{
  success: boolean;
  applicationId?: string;
  message: string;
  emailSent?: boolean;
}> => {
  try {
    // For now, use a simple approach until the pipeline is deployed
    // console.log('Submitting application data:', applicationDetail);
    const applicationId = await generateApplicationId();

    // TODO: Replace with actual pipeline call after deployment
    // const result = await client.mutations.applicationPipeline({
    //   applicationData: JSON.stringify(applicationDetail)
    // }, {
    //   authMode: 'apiKey'
    // });

    return {
      success: true,
      applicationId,
      message: 'Application submitted successfully (pipeline not yet deployed)',
      emailSent: false // TODO: Will be true after pipeline deployment
    };
  } catch (error) {
    console.error('Error submitting application:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};

/**
 * Serialize application detail to JSON string
 */
export const serializeApplicationDetail = (data: ApplicationDetail): string => {
  return JSON.stringify(data);
};

/**
 * Deserialize JSON string to application detail
 */
export const deserializeApplicationDetail = (jsonString: string): ApplicationDetail => {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    throw new Error(`Failed to parse application detail: ${error}`);
  }
};

/**
 * Create a new application data object with default values
 */
export const createApplicationData = (applicationDetail: ApplicationDetail, updatedBy?: string): ApplicationData => {
  const now = new Date().toISOString();

  return {
    applicationDetail,
    applicationStatus: 'submit success',
    status: 'ENABLED',
    createdAt: now,
    updatedAt: now,
    updatedBy: updatedBy || 'system'
  };
};

/**
 * Update application data with new values
 */
export const updateApplicationData = (
  currentData: ApplicationData,
  updates: Partial<ApplicationData>,
  updatedBy?: string
): ApplicationData => {
  const now = new Date().toISOString();

  return {
    ...currentData,
    ...updates,
    updatedAt: now,
    updatedBy: updatedBy || currentData.updatedBy || 'system'
  };
};